<script setup lang="ts">
  defineOptions({ name: 'GlobalErrorPage' })

  import { computed, toRef, useSlots } from 'vue'

  import { clearError } from 'nuxt/app'

  import { HIGHRISE_NOTIFICATION_CONFIG } from '@common/core-layer/constants'
  import OptimisedImage from '@common/media-layer/components/OptimisedImage.vue'
  import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'
  import { useGlobalErrorPageContext } from '@common/shared-ui-layer/composables/useGlobalErrorPageContext'
  import { GLOBAL_ERROR_ELEMENT_IDS, GLOBAL_ERROR_ILLUSTRATION_URL } from '@common/shared-ui-layer/constants/global-error.constants'
  import { sanitizeSupportMailtoAddress } from '@common/shared-ui-layer/utils/global-error.utils'
  import { HLContentWrap } from '@platform-ui/highrise'
  import CPButton from '@ui/components/CPButton.vue'
  import CPText from '@ui/components/CPText.vue'
  import { useI18n } from 'vue-i18n'

  import type { NuxtError } from 'nuxt/app'

  const props = defineProps({
    error: {
      type: Object as () => NuxtError,
      required: true,
    },
    homeRouteName: {
      type: String,
      default: 'index',
    },
    homeRouteParams: {
      type: Object as () => Record<string, string>,
      default: () => ({}),
    },
  })

  const { t } = useI18n()
  const { CPNavigateTo } = useCPNavigation()

  const errorInput = toRef(props, 'error')
  const { variant, statusCode, titleKey, descriptionKey, supportEmail, devStatusLabel } = useGlobalErrorPageContext(errorInput)

  const hasCustomHeader = computed(() => Boolean(useSlots().header))

  const statusBadgeLabel = computed(() => {
    const code = statusCode.value
    if (code == null || variant.value === 'generic') {
      return null
    }
    return String(code)
  })

  const isRetryPrimary = computed(() => variant.value === 'serverError' || variant.value === 'generic')

  const primaryButtonId = computed(() => (isRetryPrimary.value ? GLOBAL_ERROR_ELEMENT_IDS.RETRY : GLOBAL_ERROR_ELEMENT_IDS.RETURN_HOME))

  const primaryActionKey = computed(() => {
    if (isRetryPrimary.value) {
      return variant.value === 'generic' ? 'common.errors.generic.retry' : 'common.errors.serverError.retry'
    }
    if (variant.value === 'forbidden') {
      return 'common.errors.forbidden.returnHome'
    }
    return 'common.errors.notFound.returnHome'
  })

  const showSecondaryHome = computed(() => isRetryPrimary.value)

  const showGoBack = computed(() => variant.value === 'notFound')

  const safeSupportEmail = computed(() => sanitizeSupportMailtoAddress(supportEmail.value))

  const showContactSupport = computed(() => variant.value === 'forbidden' && Boolean(safeSupportEmail.value))

  const handleReturnHome = async () => {
    await clearError()
    await CPNavigateTo({
      name: props.homeRouteName,
      params: props.homeRouteParams,
      options: { replace: true },
    })
  }

  const handleRetry = async () => {
    await clearError()
    if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }

  const handleGoBack = async () => {
    await clearError()
    if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back()
    } else {
      await handleReturnHome()
    }
  }

  const handleContactSupport = () => {
    const email = safeSupportEmail.value
    if (email && typeof window !== 'undefined') {
      window.location.href = `mailto:${encodeURIComponent(email)}`
    }
  }

  const handlePrimaryAction = async () => {
    if (isRetryPrimary.value) {
      await handleRetry()
      return
    }
    await handleReturnHome()
  }
</script>

<template>
  <HLContentWrap class="flex min-h-dvh flex-col bg-background" :full-screen="true" :notification-config="HIGHRISE_NOTIFICATION_CONFIG">
    <slot v-if="hasCustomHeader" name="header" />

    <div
      class="flex flex-1 flex-col items-center justify-center px-4 py-12"
      :class="hasCustomHeader ? 'min-h-[calc(100dvh-3.25rem)]' : 'min-h-dvh'"
    >
      <div class="flex w-full max-w-md flex-col items-center gap-4 text-center">
        <OptimisedImage
          :id="GLOBAL_ERROR_ELEMENT_IDS.ILLUSTRATION"
          :src="GLOBAL_ERROR_ILLUSTRATION_URL"
          alt=""
          :use-raw-url="true"
          :width="155"
          :height="120"
          img-class="h-[7.5rem] w-auto"
          gradient-mode="none"
        />

        <span
          v-if="statusBadgeLabel"
          class="rounded-full border border-default px-2 py-0.5 text-xs font-semibold text-600"
          aria-hidden="true"
        >
          {{ statusBadgeLabel }}
        </span>

        <div class="flex flex-col items-center gap-1">
          <CPText tag="h1" size="2xl" weight="semibold">
            {{ t(titleKey) }}
          </CPText>
          <CPText size="lg" class="text-600 text-sm leading-normal">
            {{ t(descriptionKey) }}
          </CPText>
        </div>

        <div class="mt-2 flex flex-wrap items-center justify-center gap-3">
          <CPButton :id="primaryButtonId" type="primary" variant="primary" size="sm" class="rounded-lg" @click="handlePrimaryAction">
            {{ t(primaryActionKey) }}
          </CPButton>

          <CPButton
            v-if="showSecondaryHome"
            :id="GLOBAL_ERROR_ELEMENT_IDS.RETURN_HOME"
            type="secondary"
            variant="secondary"
            size="sm"
            class="rounded-lg"
            @click="handleReturnHome"
          >
            {{ t('common.errors.serverError.returnHome') }}
          </CPButton>

          <CPButton
            v-if="showGoBack"
            :id="GLOBAL_ERROR_ELEMENT_IDS.GO_BACK"
            type="secondary"
            variant="secondary"
            size="sm"
            class="rounded-lg"
            @click="handleGoBack"
          >
            {{ t('common.errors.notFound.goBack') }}
          </CPButton>

          <CPButton
            v-if="showContactSupport"
            :id="GLOBAL_ERROR_ELEMENT_IDS.CONTACT_SUPPORT"
            type="secondary"
            variant="secondary"
            size="sm"
            class="rounded-lg"
            @click="handleContactSupport"
          >
            {{ t('common.errors.forbidden.contactSupport') }}
          </CPButton>
        </div>

        <CPText v-if="devStatusLabel" size="xs" class="text-500 mt-4">
          {{ t('common.errors.meta.errorCode', { code: devStatusLabel }) }}
        </CPText>
      </div>
    </div>
  </HLContentWrap>
</template>
